import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Send, Briefcase, RotateCcw } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Bot avatar (Lisa - female persona, B2B advisor) - real photo provided by user
const BOT_AVATAR = "https://customer-assets.emergentagent.com/job_346f0702-303d-4720-802e-8e6b3fce771c/artifacts/ggtde7lk_kling_20260517_Build_Avatar_Friendly_m_283_2.png";

// Lightweight inline-markdown renderer:
//  - **bold**
//  - [text](url) → clickable link, opens in new tab
// Returns an array of React nodes so paragraphs preserve whitespace.
const renderRichText = (content) => {
  if (!content) return null;
  // Combined regex: link first, then bold
  const pattern = /(\[[^\]]+\]\([^)]+\))|(\*\*[^*]+\*\*)/g;
  const nodes = [];
  let lastIndex = 0;
  let match;
  let key = 0;
  while ((match = pattern.exec(content)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(content.slice(lastIndex, match.index));
    }
    const token = match[0];
    if (token.startsWith("[")) {
      const linkMatch = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(token);
      if (linkMatch) {
        nodes.push(
          <a
            key={`l-${key++}`}
            href={linkMatch[2]}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 font-semibold text-indigo-600 underline decoration-2 underline-offset-2 hover:text-indigo-800 transition-colors"
            data-testid="hypeconnect-inline-link"
          >
            {linkMatch[1]}
          </a>
        );
      } else {
        nodes.push(token);
      }
    } else {
      nodes.push(
        <strong key={`b-${key++}`} className="font-bold">
          {token.slice(2, -2)}
        </strong>
      );
    }
    lastIndex = match.index + token.length;
  }
  if (lastIndex < content.length) {
    nodes.push(content.slice(lastIndex));
  }
  return nodes;
};

const TypingIndicator = () => (
  <div className="flex items-start gap-3 animate-fade-in" data-testid="hypeconnect-typing">
    <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 border-2 border-white shadow-md bg-indigo-100">
      <img
        src={BOT_AVATAR}
        alt="Lisa"
        className="w-full h-full object-cover"
        style={{ objectPosition: "center 18%" }}
      />
    </div>
    <div className="bg-white border border-slate-100 rounded-2xl rounded-tl-sm shadow-sm px-4 py-3">
      <div className="flex gap-1.5">
        <span className="typing-dot w-2 h-2 bg-indigo-400 rounded-full"></span>
        <span className="typing-dot w-2 h-2 bg-indigo-400 rounded-full"></span>
        <span className="typing-dot w-2 h-2 bg-indigo-400 rounded-full"></span>
      </div>
    </div>
  </div>
);

const ChatBubble = ({ message, index }) => {
  const isBot = message.role === "assistant";
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: Math.min(index, 6) * 0.04 }}
      className={`flex items-start gap-3 ${isBot ? "" : "flex-row-reverse"}`}
      data-testid={`hypeconnect-msg-${message.role}-${index}`}
    >
      {isBot && (
        <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 border-2 border-white shadow-md bg-indigo-100">
          <img
            src={BOT_AVATAR}
            alt="Lisa"
            className="w-full h-full object-cover"
            style={{ objectPosition: "center 18%" }}
          />
        </div>
      )}
      <div
        className={`max-w-[85%] px-4 py-3 shadow-sm ${
          isBot
            ? "bg-white border border-slate-100 text-slate-800 rounded-2xl rounded-tl-sm"
            : "bg-gradient-to-br from-indigo-600 to-violet-600 text-white rounded-2xl rounded-tr-sm"
        }`}
      >
        <p className="text-sm leading-relaxed whitespace-pre-wrap">
          {renderRichText(message.content)}
        </p>
      </div>
    </motion.div>
  );
};

export default function HypeConnectChatPage() {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isStarting, setIsStarting] = useState(true);
  const [bottomSpacer, setBottomSpacer] = useState(0);
  const scrollRef = useRef(null);

  // Initial greeting + session
  useEffect(() => {
    startSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Smart auto-scroll:
  //  - Initial greeting / first load → stay at top so user reads from the beginning
  //  - User just sent a message → scroll to bottom so typing indicator is visible
  //  - Bot reply arrived → add a dynamic bottom spacer just big enough to let us
  //    smoothly scroll the START of the reply to the top of the chat area
  useEffect(() => {
    if (!scrollRef.current || messages.length === 0) return;
    const last = messages[messages.length - 1];

    if (isSending) {
      requestAnimationFrame(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
      });
      return;
    }

    // Only one message (greeting) → reset spacer + show from top
    if (messages.length === 1) {
      setBottomSpacer(0);
      requestAnimationFrame(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
      });
      return;
    }

    if (last.role === "assistant") {
      // Add a bottom spacer sized so we can scroll the last reply to the top
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!scrollRef.current) return;
          const node = scrollRef.current.querySelector(
            `[data-testid="hypeconnect-msg-assistant-${messages.length - 1}"]`
          );
          if (!node) return;
          const msgHeight = node.getBoundingClientRect().height;
          const containerHeight = scrollRef.current.clientHeight;
          // Spacer ensures the last message can be positioned near the top.
          // We need: container - 16 (top padding) - msgHeight worth of empty space below.
          const needed = Math.max(0, containerHeight - msgHeight - 32);
          setBottomSpacer(needed);
          // After spacer renders, scroll msg to top
          requestAnimationFrame(() => {
            if (!scrollRef.current) return;
            const containerRect = scrollRef.current.getBoundingClientRect();
            const nodeRect = node.getBoundingClientRect();
            const delta = nodeRect.top - containerRect.top - 16;
            scrollRef.current.scrollTo({
              top: scrollRef.current.scrollTop + delta,
              behavior: "smooth",
            });
          });
        });
      });
      return;
    }

    // User message just appended → keep view at bottom
    requestAnimationFrame(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTo({
          top: scrollRef.current.scrollHeight,
          behavior: "smooth",
        });
      }
    });
  }, [messages, isSending]);

  const startSession = async () => {
    setIsStarting(true);
    try {
      const res = await axios.post(`${API}/hypeconnect/session`);
      setSessionId(res.data.session_id);
      setMessages([{ role: "assistant", content: res.data.greeting }]);
    } catch (e) {
      setMessages([
        {
          role: "assistant",
          content:
            "Hoppla – die Verbindung hat gerade nicht geklappt. Bitte laden Sie die Seite neu oder schreiben Sie uns direkt an **info@hypeconnect.info**.",
        },
      ]);
    } finally {
      setIsStarting(false);
    }
  };

  const handleSend = async (e) => {
    if (e) e.preventDefault();
    const text = inputValue.trim();
    if (!text || !sessionId || isSending) return;
    setInputValue("");
    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setIsSending(true);
    try {
      const res = await axios.post(`${API}/hypeconnect/message`, {
        session_id: sessionId,
        message: text,
      });
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: res.data.response },
      ]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            "Da ist was schiefgelaufen 🙈 – mögen Sie es noch einmal versuchen?",
        },
      ]);
    } finally {
      setIsSending(false);
    }
  };

  const handleReset = () => {
    setMessages([]);
    setSessionId(null);
    setInputValue("");
    setBottomSpacer(0);
    startSession();
  };

  return (
    <div className="h-screen bg-gradient-to-br from-slate-50 via-indigo-50/40 to-violet-50/40 flex flex-col overflow-hidden">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl overflow-hidden border-2 border-white shadow-md ring-2 ring-indigo-200">
              <img
                src={BOT_AVATAR}
                alt="Lisa"
                className="w-full h-full object-cover"
                style={{ objectPosition: "center 18%" }}
              />
            </div>
            <div>
              <h1
                className="font-bold text-slate-900 text-base leading-tight"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                HypeConnect <span className="text-indigo-600">Beratung</span>
              </h1>
              <p className="text-xs text-slate-500">
                Mit <span className="font-semibold">Lisa</span> · Stellenanzeigen mit KI-Avatar-Video
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleReset}
            className="text-slate-500 hover:text-indigo-600"
            data-testid="hypeconnect-reset-button"
          >
            <RotateCcw className="w-4 h-4 mr-1" />
            Neu starten
          </Button>
        </div>
      </header>

      {/* Chat area */}
      <main
        ref={scrollRef}
        className="flex-1 overflow-y-auto"
        data-testid="hypeconnect-chat-area"
      >
        <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
          {isStarting ? (
            <TypingIndicator />
          ) : (
            <>
              {messages.map((m, i) => (
                <ChatBubble key={i} message={m} index={i} />
              ))}
              {isSending && <TypingIndicator />}
            </>
          )}

          {/* (Trust strip removed – open intro feels more natural without a sales banner) */}

          {/* Dynamic spacer enables scrolling the latest bot reply to the top of the chat area */}
          {bottomSpacer > 0 && (
            <div aria-hidden="true" style={{ height: `${bottomSpacer}px` }} />
          )}
        </div>
      </main>

      {/* Input bar */}
      <footer className="bg-white border-t border-slate-200">
        <div className="max-w-3xl mx-auto px-4 py-3">
          <form onSubmit={handleSend} className="flex items-center gap-2">
            <Input
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Stellen Sie Ihre Frage…"
              disabled={isSending || isStarting || !sessionId}
              className="flex-1 bg-slate-50 border-slate-200 focus:border-indigo-500 rounded-full px-4"
              data-testid="hypeconnect-input"
            />
            <Button
              type="submit"
              disabled={!inputValue.trim() || isSending || isStarting || !sessionId}
              className="rounded-full bg-gradient-to-br from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white shadow-md"
              data-testid="hypeconnect-send-button"
            >
              <Send className="w-4 h-4" />
            </Button>
          </form>
          <div className="flex items-center justify-center gap-2 mt-2 text-[11px] text-slate-400">
            <Briefcase className="w-3 h-3" />
            <span>Powered by HypeConnect · regional in Köln & Umgebung</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
